// Global Types
import type {
  $ExtraColumnData,
  $FullChampionshipResultTableEntry,
  $FullRegattaEntry,
  $FullRegattaEntryRequirement,
  $FullRegattaGroup,
  $FullRegattaNotification,
  $FullRegattaResultOverallEntry,
  $FullRegattaResultTable,
  $FullRegattaResultTableEntry,
  $FullRegattaResultTableRace,
  $FullRegion,
  $Model,
  $RegattaEntryResource,
  $WithRegatta,
} from 'common.data';
import type {
  $GeoLocation,
} from 'common.types';
import type {
  $DocRoute,
  $DocType,
} from 'common.enums';
import type {
  $Language,
} from 'common.language';

// Global Enums
import {
  GroupType,
  ratingKeyValue,
  RatingType,
  RegattaCoursePointType,
  RegattaGroupRatingType,
  RegattaNotificationType,
  RegattaRequirementType,
} from 'common.data';
import {
  DeploymentEnvironment,
  ErrorKey,
  resolutions,
} from 'common.enums';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import {
  CustomError,
} from '@vroskus/library-error';
import {
  getFileUrl,
} from '@vroskus/library-helpers/dist/common';

// Types
import type {
  $DateTimeFormats,
  $MandatoryConfigParams,
} from './types';

export const validateConfigParams = <C extends {
  DEPLOYMENT_ENV: DeploymentEnvironment;
}>({
    config,
    mandatoryConfigParams,
  }: {
    config: C;
    mandatoryConfigParams: $MandatoryConfigParams<C>;
  }): C => {
  if (!Object.values(DeploymentEnvironment).includes(config.DEPLOYMENT_ENV)) {
    throw new CustomError(
      'Unrecognized DEPLOYMENT_ENV value',
      ErrorKey.unrecognizedDeploymentEnvironmentError,
      {
        data: {
          value: config.DEPLOYMENT_ENV,
        },
      },
    );
  }

  const deploymentEnvironmentMandatoryConfigParams = mandatoryConfigParams[config.DEPLOYMENT_ENV];

  const emptyMandatoryParams = deploymentEnvironmentMandatoryConfigParams.filter((
    param,
  ) => !config[param] || config[param] === '');

  if (emptyMandatoryParams.length > 0) {
    throw new CustomError(
      'Unset config params',
      ErrorKey.unsetConfigParamsError,
      {
        data: {
          emptyMandatoryParams,
        },
      },
    );
  }

  return config;
};

export const getGeoCenterPosition = ({
  position1,
  position2,
}: {
  position1: $GeoLocation;
  position2: $GeoLocation;
}): $GeoLocation => ({
  lat: (position1.lat + position2.lat) / 2,
  lng: (position1.lng + position2.lng) / 2,
});

export const getMapGeoCenterPosition = ({
  data,
}: {
  data: Array<{
    Lat: number;
    Lng: number;
  }>;
}): $GeoLocation => {
  const values: Record<string, number> = {
    maxLat: _.get(
      _.maxBy(
        data,
        'Lat',
      ),
      'Lat',
      0,
    ),
    maxLon: _.get(
      _.maxBy(
        data,
        'Lng',
      ),
      'Lng',
      0,
    ),
    minLat: _.get(
      _.minBy(
        data,
        'Lat',
      ),
      'Lat',
      0,
    ),
    minLon: _.get(
      _.minBy(
        data,
        'Lng',
      ),
      'Lng',
      0,
    ),
  };

  return getGeoCenterPosition({
    position1: {
      lat: values.minLat,
      lng: values.minLon,
    },
    position2: {
      lat: values.maxLat,
      lng: values.maxLon,
    },
  });
};

export const getMapZoom = ({
  data,
}: {
  data: Array<{
    Lat: number;
    Lng: number;
  }>;
}): number => {
  const GLOBE_WIDTH = 256; // a constant in Google's map projection

  const pixelWidth = 256;

  const west = _.get(
    _.minBy(
      data,
      'Lng',
    ),
    'Lng',
    0,
  );

  const east = _.get(
    _.maxBy(
      data,
      'Lng',
    ),
    'Lng',
    0,
  );

  let angle = east - west;

  if (angle < 0) {
    angle += 360;
  }

  const zoom = Math.round(Math.log((pixelWidth * 360) / angle / GLOBE_WIDTH) / Math.LN2) - 1;

  return zoom > 16 ? 16 : zoom;
};

export const calcualteGeoDistance = ({
  position1,
  position2,
}: {
  position1: $GeoLocation;
  position2: $GeoLocation;
}): number => {
  const deg2rad = (deg: number) => deg * (Math.PI / 180);

  const R = 6371;
  const dLat = deg2rad(position2.lat - position1.lat);
  const dLon = deg2rad(position2.lng - position1.lng);
  const a1 = Math.sin(dLat / 2);
  const a2 = Math.sin(dLat / 2);
  const a3 = Math.cos(deg2rad(position1.lat));
  const a4 = Math.cos(deg2rad(position2.lat));
  const a5 = Math.sin(dLon / 2);
  const a6 = Math.sin(dLon / 2);
  const a = a1 * a2 + a3 * a4 * a5 * a6;
  const c = 2 * Math.atan2(
    Math.sqrt(a),
    Math.sqrt(1 - a),
  );
  const km = R * c; // Km

  return km / 1.852;
};

export const getRegattaCoursePath = ({
  data,
}: {
  data: Array<$Model<'RegattaCoursePoint'>>;
}): Array<$GeoLocation> => {
  const path: Array<$GeoLocation> = data.filter((
    regattaCoursePoint: $Model<'RegattaCoursePoint'>,
  ) => regattaCoursePoint.Type !== RegattaCoursePointType.independent).map((
    regattaCoursePoint: $Model<'RegattaCoursePoint'>,
  ) => {
    if (regattaCoursePoint.Type === RegattaCoursePointType.gate
      && regattaCoursePoint.Lat2
      && regattaCoursePoint.Lng2
    ) {
      return getGeoCenterPosition({
        position1: {
          lat: regattaCoursePoint.Lat,
          lng: regattaCoursePoint.Lng,
        },
        position2: {
          lat: regattaCoursePoint.Lat2,
          lng: regattaCoursePoint.Lng2,
        },
      });
    }

    return {
      lat: regattaCoursePoint.Lat,
      lng: regattaCoursePoint.Lng,
    };
  });

  return path;
};

export const getRegattaCoursePathLengths = ({
  path,
}: {
  path: Array<$GeoLocation>;
}): Array<{
  position: $GeoLocation;
  value: number;
}> => {
  const lengths: Array<{
    position: $GeoLocation;
    value: number;
  }> = [];

  let prevPosition;

  path.forEach((position: $GeoLocation) => {
    if (prevPosition) {
      const length = {
        position: getGeoCenterPosition({
          position1: position,
          position2: prevPosition,
        }),
        value: calcualteGeoDistance({
          position1: prevPosition,
          position2: position,
        }),
      };

      lengths.push(length);
    }

    prevPosition = position;
  });

  return lengths;
};

const isResolution = (value: null | string | void): boolean => {
  if (value) {
    const upperCaseValue = value.toUpperCase();

    return _.includes(
      resolutions,
      upperCaseValue,
    );
  }

  return false;
};

export const isResolutionWithPointsPosition = ({
  Position,
}: {
  Position: string;
}): {
  Points: number;
  Resolution: string;
} | null => {
  if (_.includes(
    Position,
    ';',
  )) {
    const [resolution, definedPoints] = _.split(
      Position,
      ';',
      2,
    );

    if (isResolution(resolution) && !Number.isNaN(definedPoints)) {
      return {
        Points: Number(definedPoints),
        Resolution: resolution,
      };
    }
  }

  return null;
};

const isResolutionPlain = ({
  MaxPoints,
  Position,
}: {
  MaxPoints?: number;
  Position: string;
}): {
  Points: number;
  Resolution: string;
} | null => {
  if (isResolution(Position)) {
    return {
      Points: MaxPoints || 0,
      Resolution: Position,
    };
  }

  return null;
};

export const isResolutionPosition = ({
  MaxPoints,
  Position,
}: {
  MaxPoints?: number;
  Position?: null | string;
}): {
  Points: number;
  Resolution: string;
} | null => {
  if (Position) {
    const resolutionWithPoints = isResolutionWithPointsPosition({
      Position,
    });

    if (resolutionWithPoints !== null) {
      return resolutionWithPoints;
    }

    const resolutionPlain = isResolutionPlain({
      MaxPoints,
      Position,
    });

    if (resolutionPlain !== null) {
      return resolutionPlain;
    }
  }

  return null;
};

export const isNumericPosition = ({
  Position,
}: {
  Position: null | string;
}): boolean => {
  if (Position !== null && /^\d+$/.test(Position)) {
    return true;
  }

  return false;
};

export const getRegionByRegionId = <M extends $FullRegion | $Model<'Region'>>({
  RegionId,
  regions,
}: {
  RegionId: string;
  regions: Array<M>;
}): M => {
  const region = _.find(
    regions,
    {
      id: RegionId,
    },
  ) as M;

  if (!region) {
    throw new CustomError(
      'Region is not found',
      ErrorKey.entityNotFoundError,
      {
        data: {
          RegionId,
          regions: regions.map(({
            id,
          }: M) => id),
        },
      },
    );
  }

  return region;
};

export const getRegionByRegionSeasonId = ({
  regions,
  RegionSeasonId,
}: {
  regions: Array<$FullRegion>;
  RegionSeasonId: string;
}): $FullRegion => {
  const region = _.find(
    regions,
    ({
      RegionSeasons,
    }: $FullRegion) => _.find(
      RegionSeasons,
      {
        id: RegionSeasonId,
      },
    ) as $Model<'RegionSeason'> | undefined,
  ) as $FullRegion | undefined;

  if (!region) {
    throw new CustomError(
      'Region is not found',
      ErrorKey.entityNotFoundError,
      {
        data: {
          regions: regions.map(({
            id,
          }: $FullRegion) => id),
          RegionSeasonId,
        },
      },
    );
  }

  return region;
};

export const getLatestRegionSeason = ({
  regionSeasons,
}: {
  regionSeasons: Array<$Model<'RegionSeason'>>;
}): $Model<'RegionSeason'> | null => {
  const orderedRegionSeasons: Array<$Model<'RegionSeason'>> = _.orderBy(
    regionSeasons,
    'StartDate',
    'desc',
  );

  const lastRegionSeason: $Model<'RegionSeason'> | undefined = _.first(orderedRegionSeasons);

  return lastRegionSeason || null;
};

export const getLatestRegionSeasonByRegionId = ({
  RegionId,
  regions,
}: {
  RegionId: string;
  regions: Array<$FullRegion>;
}): $Model<'RegionSeason'> | null => {
  const region = getRegionByRegionId({
    RegionId,
    regions,
  });

  return getLatestRegionSeason({
    regionSeasons: region.RegionSeasons,
  });
};

export const isLatestRegionSeason = ({
  regions,
  RegionSeasonId,
}: {
  regions: Array<$FullRegion>;
  RegionSeasonId: string;
}): boolean => {
  const region = getRegionByRegionSeasonId({
    regions,
    RegionSeasonId,
  });

  const lastRegionSeason: $Model<'RegionSeason'> | null = getLatestRegionSeason({
    regionSeasons: region.RegionSeasons,
  });

  return lastRegionSeason !== null && lastRegionSeason.id === RegionSeasonId;
};

export const getPreviousRegionSeasonId = ({
  regions,
  RegionSeasonId,
}: {
  regions: Array<$FullRegion>;
  RegionSeasonId: string;
}): null | string => {
  const region = getRegionByRegionSeasonId({
    regions,
    RegionSeasonId,
  });

  const previousIndex: number = _.findIndex(
    region.RegionSeasons,
    {
      id: RegionSeasonId,
    },
  ) + 1;

  return _.get(
    region,
    `RegionSeasons[${previousIndex}].id`,
  ) || null;
};

export const getRegionUrl = ({
  mainDomain,
  RegionId,
  regions,
}: {
  mainDomain: string;
  RegionId: string;
  regions: Array<$FullRegion>;
}): string => {
  const region = _.find(
    regions,
    {
      id: RegionId,
    },
  ) as $FullRegion | undefined;

  const domain: string | undefined = _.get(
    region,
    'Domain',
  );

  const urlPart: string = typeof domain !== 'undefined' ? `${domain}.` : '';

  return `https://${urlPart}${mainDomain}`;
};

export const getRegionSeasonUrl = ({
  mainDomain,
  regions,
  RegionSeasonId,
}: {
  mainDomain: string;
  regions: Array<$FullRegion>;
  RegionSeasonId: string;
}): string => {
  const region = getRegionByRegionSeasonId({
    regions,
    RegionSeasonId,
  });

  let regionUrl: string = getRegionUrl({
    mainDomain,
    RegionId: region.id,
    regions,
  });

  if (!isLatestRegionSeason({
    regions,
    RegionSeasonId,
  })) {
    const regionSeason = _.find(
      region.RegionSeasons,
      {
        id: RegionSeasonId,
      },
    ) as $Model<'RegionSeason'> | undefined;

    if (regionSeason) {
      regionUrl = `${regionUrl}/${regionSeason.Name}`;
    }
  }

  return regionUrl;
};

export const getRegionSeasonByRegionSeasonId = ({
  regions,
  RegionSeasonId,
}: {
  regions: Array<$FullRegion>;
  RegionSeasonId: string;
}): $Model<'RegionSeason'> => {
  const region = getRegionByRegionSeasonId({
    regions,
    RegionSeasonId,
  });

  const regionSeason = _.find(
    region.RegionSeasons,
    {
      id: RegionSeasonId,
    },
  ) as $Model<'RegionSeason'> | undefined;

  if (!regionSeason) {
    throw new CustomError(
      'RegionSeason is not found',
      ErrorKey.entityNotFoundError,
      {
        data: {
          regions: regions.map(({
            id,
          }: $FullRegion) => id),
          RegionSeasonId,
        },
      },
    );
  }

  return regionSeason;
};

export const isRegionSeasonName = ({
  regions,
  value,
}: {
  regions: Array<$FullRegion>;
  value?: string;
}): boolean => {
  const found = regions.filter(
    (region: $FullRegion) => region.RegionSeasons.filter(
      (regionSeason: $Model<'RegionSeason'>) => regionSeason.Name === value,
    ).length > 0,
  );

  return found.length > 0;
};

export const getDateTimeFormats = ({
  RegionId,
  regions,
}: {
  RegionId: null | string;
  regions: Array<$FullRegion | $Model<'Region'>>;
}): $DateTimeFormats => {
  if (RegionId !== null) {
    const region = getRegionByRegionId({
      RegionId,
      regions,
    });

    return {
      Date: region.DateFormat,
      Short: region.ShortDateFormat,
      Time: region.TimeFormat,
      Timezone: region.Timezone,
    };
  }

  return {
    Date: 'YYYY-MM-DD',
    Short: 'MM.DD',
    Time: 'HH:mm',
    Timezone: 'UTC',
  };
};

export const getShortItemUrl = ({
  basePath,
  item,
  mainDomain,
  regions,
}: {
  basePath: '/championship' | '/regatta';
  item: {
    id: string;
    RegionSeasonId: string;
    Slug?: null | string;
  };
  mainDomain: string;
  regions: Array<$FullRegion>;
}): string => {
  const {
    id,
    RegionSeasonId,
    Slug,
  } = item;

  const regionSeason = getRegionSeasonByRegionSeasonId({
    regions,
    RegionSeasonId,
  });

  const regionUrl: string = getRegionUrl({
    mainDomain,
    RegionId: regionSeason.RegionId,
    regions,
  });

  let baseUrl = regionUrl;

  if (!isLatestRegionSeason({
    regions,
    RegionSeasonId,
  })) {
    baseUrl = `${baseUrl}${basePath}/${regionSeason.Name}`;
  }

  return `${baseUrl}/${Slug || id}`;
};

export const getFullItemUrl = ({
  basePath,
  item,
  mainDomain,
  regions,
}: {
  basePath: '/championship' | '/regatta';
  item: {
    id: string;
    RegionSeasonId: string;
    Slug?: null | string;
  };
  mainDomain: string;
  regions: Array<$FullRegion>;
}): string => {
  const {
    id,
    RegionSeasonId,
    Slug,
  } = item;

  const regionSeason = getRegionSeasonByRegionSeasonId({
    regions,
    RegionSeasonId,
  });

  const regionUrl: string = getRegionUrl({
    mainDomain,
    RegionId: regionSeason.RegionId,
    regions,
  });

  const baseUrl = `${regionUrl}${basePath}`;

  if (Slug) {
    return `${baseUrl}/${regionSeason.Name}/${Slug}`;
  }

  return `${baseUrl}/${id}`;
};

export const getDocumentUrl = ({
  cdnUrl,
  docRoute,
  docType,
  id,
  language,
}: {
  cdnUrl: string;
  docRoute: $DocRoute;
  docType: $DocType;
  id: string;
  language: $Language;
}): string => `${cdnUrl}/docs/${language}/${docType}/${docRoute}/${id}`;

export const shortenText = (text: string, max: number): string => {
  if (text.length > max) {
    return `${text.substr(
      0,
      max - 3,
    )}...`;
  }

  return text;
};

export const getRegattaNotificationContent = ({
  regattaNotification,
  t,
}: {
  regattaNotification: $FullRegattaNotification | $Model<'RegattaNotification'>;
  t: (arg0: string) => string;
}): {
  body: string;
  title: string;
} => {
  const content = {
    [RegattaNotificationType.regattaEntryCrewmember]: () => ({
      body: t(''),
      title: t(''),
    }),
    [RegattaNotificationType.regattaMessage]: () => ({
      body: t('A new regatta message has been submitted, please check the regatta page.'),
      title: t('Regatta message submitted'),
    }),
    [RegattaNotificationType.regattaProtest]: () => ({
      body: t('New regatta protest involving your presistance has been submitted.'),
      title: t('Regatta protest submitted'),
    }),
    [RegattaNotificationType.regattaProtestEnd]: () => ({
      body: t('Regatta protest involving your presistance has received a resolution.'),
      title: t('Regatta protest resolution'),
    }),
    [RegattaNotificationType.regattaResultTableRacePublished]: () => ({
      body: t(''),
      title: t('Regatta results published'),
    }),
    [RegattaNotificationType.regattaSettingsChanged]: () => ({
      body: t('Some regatta settings information has changed, please check the regatta page.'),
      title: t('Regatta info updated'),
    }),
    [RegattaNotificationType.regattaTrackingOn]: () => ({
      body: t(''),
      title: t(''),
    }),
  };

  return content[regattaNotification.Type]();
};

export const formatTimeRange = ({
  end,
  start,
}: {
  end: Date;
  start: Date;
}): string => {
  const startDateMoment: moment.Moment = moment(start);
  const endDateMoment: moment.Moment = moment(end);
  const startDateFormatted: string = startDateMoment.format('HH:mm');
  const endDateFormatted: string = endDateMoment.format('HH:mm');
  const days: number = endDateMoment.diff(
    startDateMoment,
    'days',
  );

  let output = `${startDateFormatted} - ${endDateFormatted}`;

  if (days > 0) {
    output = `${output} +(${days})`;
  }

  return output;
};

export const formatDateRange = ({
  end,
  shortDateFormat,
  // MM.DD | DD.MM
  start,
}: {
  end: Date;
  shortDateFormat: string;
  start: Date;
}): string => {
  const startDateMoment: moment.Moment = moment(start);
  const endDateMoment: moment.Moment = moment(end);
  const startDateFormatted: string = startDateMoment.format(shortDateFormat);
  const endDateFormatted: string = endDateMoment.format(shortDateFormat);

  if (startDateFormatted === endDateFormatted) {
    return startDateFormatted;
  }

  if (startDateMoment.isSame(
    endDateMoment,
    'month',
  ) === true) {
    const endDateDay: string = endDateMoment.format('DD');

    if (shortDateFormat === 'DD.MM') {
      return `${endDateDay}-${startDateFormatted}`;
    }

    return `${startDateFormatted}-${endDateDay}`;
  }

  return `${startDateFormatted} - ${endDateFormatted}`;
};

export const formatDateTime = ({
  dateFormat,
  timeFormat,
  value,
}: {
  dateFormat: string;
  timeFormat: string;
  value?: Date | null | string;
}): string => {
  const format = `${dateFormat} ${timeFormat}`;

  if (value) {
    return moment.utc(value).local().format(format);
  }

  return '';
};

export const formatTime = ({
  timeFormat,
  value,
}: {
  timeFormat: string;
  value?: Date | null | string;
}): string => {
  if (value) {
    return moment.utc(value).local().format(timeFormat);
  }

  return '';
};

export const getResultTimeString = ({
  race,
  result,
}: {
  race: $FullRegattaResultTableRace;
  result: $Model<'RegattaResultTableRaceResult'>;
}): string => {
  const startDateString = `${race.Year}-${race.Month}-${race.Day} ${race.Hour}:${race.Minute}:${race.Second}`;
  const startDate: moment.Moment = moment(
    startDateString,
    'YYYY-M-D H:m:s',
    true,
  );
  const finishDateString = `${race.Year}-${_.get(
    result,
    'Month',
  )}-${_.get(
    result,
    'Day',
  )} ${_.get(
    result,
    'Hour',
  )}:${_.get(
    result,
    'Minute',
  )}:${_.get(
    result,
    'Second',
  )}`;
  const finishDate: moment.Moment = moment(
    finishDateString,
    'YYYY-M-D H:m:s',
    true,
  );

  if (finishDate.isValid() === true) {
    const finishDateText = finishDate.format('HH:mm:ss');

    if (finishDate.isSame(
      startDate,
      'day',
    ) !== true) {
      const days: number = finishDate.diff(
        startDate,
        'days',
      );

      if (days > 0) {
        return `+${days}d ${finishDateText}`;
      }
    }

    return finishDateText;
  }

  return '-';
};

export const getResultPlaceString = ({
  result,
}: {
  result: $Model<'RegattaResultTableRaceResult'>;
}): string => {
  let place = result.Place;

  const resolutionPosition = isResolutionPosition({
    Position: result.Position,
  });

  if (resolutionPosition !== null) {
    place = resolutionPosition.Resolution;
  }

  return place || '-';
};

export const getResultByEntry = ({
  entryId,
  results,
}: {
  entryId: string;
  results: Array<$Model<'RegattaResultTableRaceResult'>>;
}): $Model<'RegattaResultTableRaceResult'> => {
  const result = _.find(
    results,
    {
      RegattaResultTableEntryId: entryId,
    },
  ) as $Model<'RegattaResultTableRaceResult'> | undefined;

  if (!result) {
    throw new CustomError(
      'RegattaResultTableRaceResult is not found',
      ErrorKey.entityNotFoundError,
      {
        data: {
          entryId,
        },
      },
    );
  }

  return result;
};

export const getRegattaEntrySkipperName = ({
  regattaEntry,
}: {
  regattaEntry: $FullRegattaEntry | $RegattaEntryResource;
}): string => _.get(
  regattaEntry,
  'RegattaEntryCrewmembers[0].Name',
) || '';

export const getChampionshipEntryTitle = ({
  championshipResultTableEntry,
}: {
  championshipResultTableEntry: $FullChampionshipResultTableEntry;
}): string => {
  const regattaEntry = championshipResultTableEntry.RegattaEntry;

  if (regattaEntry.Name) {
    return championshipResultTableEntry.NameOverride || regattaEntry.Name;
  }

  return regattaEntry.RegistrationNo;
};

export const getChampionshipEntrySubTitle = ({
  championshipResultTableEntry,
}: {
  championshipResultTableEntry: $FullChampionshipResultTableEntry;
}): string => {
  const regattaEntry = championshipResultTableEntry.RegattaEntry;

  if (regattaEntry.Name) {
    return regattaEntry.RegistrationNo;
  }

  return championshipResultTableEntry.NameOverride || regattaEntry.Name || '';
};

// eslint-disable-next-line complexity
export const getRegattaEntryParams = ({
  bowNoEnabled,
  countryEnabled,
  regattaEntry,
}: {
  bowNoEnabled: boolean;
  countryEnabled: boolean;
  regattaEntry: $Model<'RegattaEntry'>;
}): {
  bowNo: null | string;
  bowNoRegNoString: string;
  bowNoString: string;
  countryAlpha3: string;
  fullString: string;
  name: null | string;
  regNo: string;
  sailNo: null | string;
} => {
  const output = {
    bowNo: null,
    bowNoRegNoString: '',
    bowNoString: '',
    countryAlpha3: '',
    fullString: '',
    name: regattaEntry.Name,
    regNo: regattaEntry.RegistrationNo,
    sailNo: regattaEntry.SailNo,
  };

  if (bowNoEnabled === true) {
    if (regattaEntry.BowNo) {
      output.bowNo = regattaEntry.BowNo;
    } else {
      output.bowNo = '#';
    }

    output.bowNoString = `${output.bowNo} | `;
    output.bowNoRegNoString = output.bowNoString;
  }

  if (output.regNo.length > 2) {
    output.countryAlpha3 = output.regNo.slice(
      0,
      -2,
    );
  }

  if (countryEnabled === true) {
    output.regNo = output.sailNo || output.countryAlpha3;
  }

  output.bowNoRegNoString += output.regNo;

  output.fullString += output.bowNoRegNoString;

  if (output.name) {
    output.fullString += ` ${output.name}`;
  }

  return output;
};

export const getRegattaEntryRegistrationNo = ({
  countryEnabled,
  regattaEntry,
}: {
  countryEnabled: boolean;
  regattaEntry: $Model<'RegattaEntry'>;
}): string => {
  const {
    regNo,
  } = getRegattaEntryParams({
    bowNoEnabled: false,
    countryEnabled,
    regattaEntry,
  });

  return regNo;
};

export const getRegattaEntryCrewmemberNames = ({
  excludeSkipper,
  regattaEntry,
  showAge,
}: {
  excludeSkipper?: boolean;
  regattaEntry: $FullRegattaEntry | $RegattaEntryResource;
  showAge?: boolean;
}): Array<string> => {
  const regattaEntryCrewmembers: Array<$Model<'RegattaEntryCrewmember'>> = regattaEntry
    .RegattaEntryCrewmembers || [];

  const crew: Array<string> = regattaEntryCrewmembers.map(({
    Birthdate,
    Name,
  }) => {
    if (showAge === true && Birthdate !== null) {
      const age = moment().diff(
        moment(Birthdate),
        'years',
        false,
      );

      return `${Name} (${age})`;
    }

    return Name;
  });

  if (excludeSkipper === true) {
    _.tail(crew);
  }

  return crew;
};

export const getRegattaEntryTitle = ({
  bowNoEnabled,
  countryEnabled,
  regattaEntry,
}: {
  bowNoEnabled: boolean;
  countryEnabled: boolean;
  regattaEntry: $RegattaEntryResource;
}): string => {
  const isSportGroup: boolean = _.get(
    regattaEntry,
    'RegattaGroup.Group.Type',
  ) === GroupType.sport;

  const {
    bowNoRegNoString,
    bowNoString,
    name,
  } = getRegattaEntryParams({
    bowNoEnabled,
    countryEnabled,
    regattaEntry,
  });

  if (isSportGroup === true) {
    return bowNoRegNoString;
  }

  return `${bowNoString} ${name || ''}`;
};

export const getRegattaEntrySubTitle = ({
  countryEnabled,
  regattaEntry,
  showAge,
}: {
  countryEnabled: boolean;
  regattaEntry: $FullRegattaEntry | $RegattaEntryResource;
  showAge?: boolean;
}): string => {
  const isSportGroup: boolean = _.get(
    regattaEntry,
    'RegattaGroup.Group.Type',
  ) === GroupType.sport;

  if (isSportGroup === true) {
    const crew = getRegattaEntryCrewmemberNames({
      excludeSkipper: true,
      regattaEntry,
      showAge,
    });

    return crew.join(', ');
  }

  const skipperString: string = getRegattaEntrySkipperName({
    regattaEntry,
  });

  const registrationNo: string = getRegattaEntryRegistrationNo({
    countryEnabled,
    regattaEntry,
  });

  return `${registrationNo} | ${skipperString}`;
};

export const cleanRatings = ({
  ratings,
}: {
  ratings: Array<$Model<'Rating'>>;
}): Array<$Model<'Rating'>> => {
  const sortedRatings = _.orderBy(
    ratings,
    ['createdAt'],
    ['desc'],
  );

  return _.uniqBy(
    sortedRatings,
    'Type',
  );
};

export const getRating = ({
  ratings,
  type,
}: {
  ratings: Array<$Model<'Rating'>>;
  type: RatingType | RegattaGroupRatingType;
}): $Model<'Rating'> | undefined => {
  const cleanedRatings = cleanRatings({
    ratings,
  });

  return _.find(
    cleanedRatings,
    {
      Type: type,
    },
  ) as $Model<'Rating'> | undefined;
};

export const getRegattaEntryRatingValue = ({
  regattaEntry,
  type,
}: {
  regattaEntry: $FullRegattaEntry | $RegattaEntryResource;
  type: RatingType | RegattaGroupRatingType;
}): string => {
  const rating = getRating({
    ratings: regattaEntry.Ratings,
    type,
  });

  const ratingKeyValueValue: string | undefined = _.get(
    rating,
    'KeyValue' as string,
  );

  return ratingKeyValueValue || '';
};

export const getExtraColumnData = ({
  regattaResultTable,
}: {
  regattaResultTable: $FullRegattaResultTable | $WithRegatta<$FullRegattaResultTable>;
}): $ExtraColumnData => {
  const regattaResultTableRegattaGroupRatingType: RegattaGroupRatingType = _.get(
    regattaResultTable,
    'RegattaGroup.RatingType',
    RegattaGroupRatingType.monotype,
  );

  if (regattaResultTableRegattaGroupRatingType !== RegattaGroupRatingType.monotype) {
    const ratingKeyValueName: string = ratingKeyValue[regattaResultTableRegattaGroupRatingType];

    return {
      name: ratingKeyValueName,
      regattaGroupRatingType: regattaResultTableRegattaGroupRatingType,
      type: 'rating',
    };
  }

  const regattaResultTableRegattaGroupGroupType: GroupType = _.get(
    regattaResultTable,
    'RegattaGroup.Group.Type',
    GroupType.cruiser,
  );

  if (regattaResultTableRegattaGroupGroupType === GroupType.sport) {
    const regattaResultTableEntriesRegattaGroupIds: Array<null | string> = regattaResultTable
      .RegattaResultTableEntries.map(({
        RegattaEntry,
      }: $FullRegattaResultTableEntry) => RegattaEntry.RegattaGroupId);

    const uniqueRegattaGroupsQuantity = _.uniq(regattaResultTableEntriesRegattaGroupIds).length;

    if (uniqueRegattaGroupsQuantity > 1) {
      return {
        name: 'Group',
        regattaGroupRatingType: regattaResultTableRegattaGroupRatingType,
        type: 'group',
      };
    }
  }

  return {
    name: '',
    regattaGroupRatingType: regattaResultTableRegattaGroupRatingType,
    type: null,
  };
};

export const getExtraColumnValue = ({
  extraColumnData,
  regattaEntry,
}: {
  extraColumnData: $ExtraColumnData;
  regattaEntry: $FullRegattaEntry | $RegattaEntryResource;
}): string => {
  if (extraColumnData.type === 'rating') {
    const ratingValue: string = getRegattaEntryRatingValue({
      regattaEntry,
      type: extraColumnData.regattaGroupRatingType,
    });

    return ratingValue;
  }

  if (extraColumnData.type === 'group') {
    const regattaEntryRegattaGroup: $FullRegattaGroup | undefined = _.get(
      regattaEntry,
      'RegattaGroup',
    );

    const regattaEntryRegattaGroupName: string = _.get(
      regattaEntryRegattaGroup,
      'AltName',
    ) || _.get(
      regattaEntryRegattaGroup,
      'Group.Name',
      '',
    );

    return regattaEntryRegattaGroupName;
  }

  return '';
};

export const getTimeDiff = (value: number, values: Array<number>): string => {
  if (value === 0) {
    return '-';
  }

  const nonZeroValues: Array<number> = values.filter((i) => i !== 0);

  const minValue: number | undefined = _.min(nonZeroValues);

  let diffString: string = '';

  if (typeof minValue !== 'undefined') {
    const diff = value - minValue;

    diffString = diff === 0 ? '' : `+${diff}s`;
  }

  return diffString;
};

export const durationFormat = (value: number): string => {
  let hours: number = value / 3600;
  let minutes: number = (value % 3600) / 60;
  let seconds: number = (minutes * 60) % 60;

  hours = Math.trunc(hours);
  minutes = Math.trunc(minutes);
  seconds = Math.trunc(seconds);

  const padHours: string = String(hours).padStart(
    2,
    '0',
  );
  const padMinutes: string = String(minutes).padStart(
    2,
    '0',
  );
  const padSeconds: string = String(seconds).padStart(
    2,
    '0',
  );

  return `${padHours}:${padMinutes}:${padSeconds}`;
};

export const getRegattaEntryRequirementValue = ({
  cdnUrl,
  regattaEntryRequirement,
}: {
  cdnUrl: string;
  regattaEntryRequirement: $FullRegattaEntryRequirement;
}): null | string => {
  const typeValue = {
    [RegattaRequirementType.boolean]: () => (regattaEntryRequirement.Value === 'true' ? 'Yes' : 'No'),
    [RegattaRequirementType.file]: () => (regattaEntryRequirement.Value ? getFileUrl({
      cdnUrl,
      file: regattaEntryRequirement.Value,
    }) : null),
    [RegattaRequirementType.fileSource]: () => (regattaEntryRequirement.Value ? getFileUrl({
      cdnUrl,
      file: regattaEntryRequirement.Value,
    }) : null),
    [RegattaRequirementType.select]: () => {
      if (regattaEntryRequirement.Value) {
        const selectValue = JSON.parse(regattaEntryRequirement.Value);

        if (selectValue) {
          return selectValue.label;
        }
      }

      return null;
    },
    [RegattaRequirementType.value]: () => regattaEntryRequirement.Value,
    [RegattaRequirementType.valueUnique]: () => regattaEntryRequirement.Value,
  };

  const {
    RegattaRequirement,
  } = regattaEntryRequirement;

  if (RegattaRequirement) {
    return typeValue[RegattaRequirement.Type]() || null;
  }

  return null;
};

export const isSeasonalRating = (
  type: RatingType,
): boolean => typeof _.find(
  [RatingType.orc],
  type,
) !== 'undefined';

export const sortResults = <I extends Array<
$FullChampionshipResultTableEntry | $FullRegattaResultOverallEntry | $FullRegattaResultTableEntry
>>(
    input: I,
    invert?: boolean,
  ): I => _.orderBy(
    input,
    [
      (item): number => item.Points,
      (item): number => {
        if (item.Place && item.Place.includes('-')) {
          const [place]: Array<string> = item.Place.split('-');

          return Number(place);
        }

        return Number(item.Place);
      },
    ],
    invert ? ['desc', 'desc'] : ['asc', 'asc'],
  ) as I;

export const getDateRange = ({
  data,
  extents,
  timezone,
}: {
  data: Array<Date | moment.Moment | string>;
  extents?: boolean;
  timezone: string;
}): {
  max?: Date;
  min?: Date;
} => {
  const range: {
    max?: Date;
    min?: Date;
  } = {
    max: undefined,
    min: undefined,
  };

  if (data.length > 0) {
    const moments: Array<moment.Moment> = data.map((d) => moment(d));
    let minDate: moment.Moment = moment.min(moments);
    let maxDate: moment.Moment = moment.max(moments);

    if (extents) {
      minDate = minDate.startOf('day');
      maxDate = maxDate.endOf('day');
    }

    range.min = momentTimezone.tz(
      minDate,
      timezone,
    ).toDate();
    range.max = momentTimezone.tz(
      maxDate,
      timezone,
    ).toDate();
  }

  return range;
};
